<template>
  <div class="grid gap-x-4 gap-y-2 md:grid-cols-2">
    <div
      class="flex cursor-pointer flex-col gap-1"
      data-testid="start-date"
      @click="showPicker = !disabled ? !showPicker : showPicker"
    >
      <h4>{{ startDateTitle }}</h4>
      <span
        :class="input({ size: 'sm', class: 'font-normal' })"
        role="textbox"
        data-testid="start-time"
        >{{ formattedStartDate }}</span
      >
    </div>

    <div
      class="flex cursor-pointer flex-col gap-1"
      data-testid="end-date"
      @click="showPicker = !disabled ? !showPicker : showPicker"
    >
      <h4>{{ endDateTitle }}</h4>
      <span
        :class="input({ size: 'sm', class: 'font-normal' })"
        role="textbox"
        data-testid="end-time"
        >{{ formattedEndDate }}</span
      >
    </div>

    <div v-if="showPicker" ref="pickerContainer" class="absolute z-10 mt-11">
      <Datepicker
        v-model="dates"
        range
        inline
        week-numbers
        :close-on-auto-apply="false"
        :partial-range="false"
        :month-change-on-scroll="false"
        :clearable="false"
        enable-time-picker
        :flow="['calendar', 'time']"
        :disabled-dates="disabledDates"
        :day-class="getDayClass"
        :min-date="minDate"
        :max-date="maxDate"
        :no-disabled-range="noDisabledRange"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { formatDate } from '@/hooks/use-formatted-date';
import { onClickOutside } from '@vueuse/core';
import { computed, ref, watchEffect } from 'vue';
import Datepicker from '@/components/Datepicker.vue';
import { input } from '@carvia/theme/lib/cva/input.ts';

const props = defineProps<{
  startDate: Date | string;
  endDate: Date | string;
  bookingId?: string;
  disabled?: boolean;
  startDateTitle: string;
  endDateTitle: string;
  disabledDates?: ((date: Date) => boolean) | Date[];
  highlightedDates?: (date: Date) => boolean;
  minDate?: Date | string;
  maxDate?: Date | string;
  noDisabledRange?: boolean;
}>();
const emit = defineEmits<{
  (e: 'update:startDate', value: string): void;
  (e: 'update:endDate', value: string): void;
  (e: 'change', startDate: string, endDate: string): void;
}>();

const dates = ref([
  props.startDate ? new Date(props.startDate) : undefined,
  props.endDate ? new Date(props.endDate) : undefined,
]);
watchEffect(
  () =>
    (dates.value = [
      props.startDate ? new Date(props.startDate) : undefined,
      props.endDate ? new Date(props.endDate) : undefined,
    ]),
);
const showPicker = ref(false);
const pickerContainer = ref(null);
onClickOutside(pickerContainer, () => {
  if (!showPicker.value) return;
  showPicker.value = false;

  if (
    props.startDate === dates.value[0]?.toISOString() &&
    props.endDate === dates.value[1]?.toISOString()
  ) {
    return;
  }

  dates.value[0] && emit('update:startDate', dates.value[0].toISOString());
  dates.value[1] && emit('update:endDate', dates.value[1].toISOString());
  dates.value[0] &&
    dates.value[1] &&
    emit('change', dates.value[0].toISOString(), dates.value[1].toISOString());
});

const formattedStartDate = computed(() => formatDate(props.startDate));
const formattedEndDate = computed(() => formatDate(props.endDate));

const getDayClass = (date: Date) => (props.highlightedDates?.(date) ? 'dp__blocked-day' : '');
</script>
